// The chat panel's settings: the user's own Anthropic or OpenAI key, which
// model, and how hard it thinks, by each provider's own effort levels. Each key
// lives in this browser and is only ever sent to its own provider. Nothing here
// loads the SDK, so the rest of the app stays small. The model each agent uses
// by default is the board's (Settings, Agents), so it follows the user to every
// device and the board's jobs and buttons start on it too.

import { BUILT_IN, defaultFor, runModel } from "../../shared/agentModels";
import { api } from "../lib/api";
import { get as board, set as setBoard } from "../state/store";
import { readStored, removeStored, writeStored } from "../lib/stored";

/** Who answers in the chat: Claude on an Anthropic key, or Codex on an OpenAI key. */
export type Provider = "anthropic" | "openai";
/** Effort levels as each API names them: how much the model thinks and how many tokens it spends. None is OpenAI's, for Luna. */
export type Effort = "none" | "low" | "medium" | "high" | "xhigh" | "max";

export interface ModelInfo {
  id: string;
  provider: Provider;
  label: string;
  short: string;
  blurb: string;
  /** US dollars per million tokens, Anthropic's list price on 11 Oct 2026; null for a model this build does not know. */
  price: { input: number; output: number; cacheRead: number } | null;
  /** the effort levels it takes, none when it has no effort setting */
  efforts: Effort[];
  /** what the API does when no effort is sent */
  defaultEffort: Effort | null;
  adaptive: boolean; // adaptive thinking; the newest models always think
  budget: boolean; // thinks only with a token budget, switched on or off
  fallbacks: boolean; // server-side fallback when its safety checks decline
  maxTokens: number;
  /** web search, by the tool version the model takes */
  search: "web_search_20260209" | "web_search_20250305" | null;
  /** a model the key lists that came out after this build, read from Anthropic's model list */
  newer?: boolean;
}

const ALL: Effort[] = ["low", "medium", "high", "xhigh", "max"];

export const MODELS: ModelInfo[] = [
  {
    id: "claude-fable-5-1",
    label: "Claude Fable 5.1",
    short: "Fable 5.1",
    provider: "anthropic",
    blurb: "Anthropic's most capable. For the hardest builds.",
    price: { input: 10, output: 50, cacheRead: 0.25 },
    efforts: ALL,
    defaultEffort: "high",
    adaptive: true,
    budget: false,
    fallbacks: true,
    maxTokens: 64000,
    search: "web_search_20260209",
  },
  {
    id: "claude-opus-5-5",
    label: "Claude Opus 5.5",
    short: "Opus 5.5",
    provider: "anthropic",
    blurb: "The newest Opus. Strong, and lower priced than Opus 5.",
    price: { input: 4, output: 20, cacheRead: 0.2 },
    efforts: ALL,
    defaultEffort: "medium",
    adaptive: true,
    budget: false,
    fallbacks: true,
    maxTokens: 64000,
    search: "web_search_20260209",
  },
  {
    id: "claude-opus-5",
    label: "Claude Opus 5",
    short: "Opus 5",
    provider: "anthropic",
    blurb: "Deep reasoning, for building whole sections.",
    price: { input: 5, output: 25, cacheRead: 0.5 },
    efforts: ALL,
    defaultEffort: "high",
    adaptive: true,
    budget: false,
    fallbacks: true,
    maxTokens: 64000,
    search: "web_search_20260209",
  },
  {
    id: "claude-sonnet-5",
    label: "Claude Sonnet 5",
    short: "Sonnet 5",
    provider: "anthropic",
    blurb: "Fast and capable, for less than half the price.",
    price: { input: 2, output: 10, cacheRead: 0.2 },
    efforts: ALL,
    defaultEffort: "high",
    adaptive: true,
    budget: false,
    fallbacks: false,
    maxTokens: 64000,
    search: "web_search_20260209",
  },
  {
    id: "claude-haiku-4-5",
    label: "Claude Haiku 4.5",
    short: "Haiku 4.5",
    provider: "anthropic",
    blurb: "The quickest and cheapest, for small changes.",
    price: { input: 1, output: 5, cacheRead: 0.1 },
    efforts: [],
    defaultEffort: null,
    adaptive: false,
    budget: true,
    fallbacks: false,
    maxTokens: 32000,
    search: "web_search_20250305",
  },
];

/**
 * Codex, as OpenAI lists it on 11 Oct 2026: the three models the Codex app
 * offers, all in the API, with OpenAI's own effort levels (Luna alone takes
 * none) and its list prices. Cached input is priced at the full input rate,
 * since OpenAI's cached price for these was not confirmed: the estimate errs high.
 */
export const CODEX_MODELS: ModelInfo[] = [
  { id: "gpt-6-astra", provider: "openai", label: "GPT-6 Astra", short: "GPT-6 Astra", blurb: "OpenAI's most capable, for the hardest builds.", price: { input: 10, output: 50, cacheRead: 10 }, efforts: ["low", "medium", "high", "xhigh", "max"], defaultEffort: "medium", adaptive: false, budget: false, fallbacks: false, maxTokens: 32000, search: null },
  { id: "gpt-6.1-sol", provider: "openai", label: "GPT-6.1 Sol", short: "GPT-6.1 Sol", blurb: "Near Astra, at a fifth of the price.", price: { input: 2, output: 10, cacheRead: 2 }, efforts: ["low", "medium", "high", "xhigh", "max"], defaultEffort: "medium", adaptive: false, budget: false, fallbacks: false, maxTokens: 32000, search: null },
  { id: "gpt-6-luna", provider: "openai", label: "GPT-6 Luna", short: "GPT-6 Luna", blurb: "The quickest and cheapest, for small changes.", price: { input: 0.1, output: 0.5, cacheRead: 0.1 }, efforts: ["none", "low", "medium", "high", "xhigh", "max"], defaultEffort: "medium", adaptive: false, budget: false, fallbacks: false, maxTokens: 32000, search: null },
];

export const MODEL: Record<string, ModelInfo> = Object.fromEntries([...MODELS, ...CODEX_MODELS].map((m) => [m.id, m]));

/** The models a provider offers, in the order the picker shows them. */
export const modelsOf = (p: Provider) => (p === "openai" ? CODEX_MODELS : MODELS);
/** What each agent starts on until the user chooses a default: the chat, a Button and a job alike (shared/agentModels.ts). */
export const DEFAULT_MODEL: Record<Provider, string> = { anthropic: BUILT_IN.claude, openai: BUILT_IN.codex };
/** What the chat calls itself on the board, so its changes read as its own. */
export const chatName = (p: Provider) => (p === "openai" ? "Codex" : "Claude");

export const EFFORTS: { id: Effort; label: string; blurb: string }[] = [
  { id: "none", label: "None", blurb: "No reasoning at all. The fastest, for the smallest edits." },
  { id: "low", label: "Low", blurb: "The fastest, with the least thinking. For small edits." },
  { id: "medium", label: "Medium", blurb: "Quick, and careful with layout." },
  { id: "high", label: "High", blurb: "Thinks things through. Right for most builds." },
  { id: "xhigh", label: "Extra high", blurb: "Thinks longer. For custom widgets and whole sections." },
  { id: "max", label: "Max", blurb: "The most thinking. Slowest, and costs the most." },
];

/** A model Anthropic lists for this key that this build has no entry for: newer, so it takes the newest models' settings. */
export function unknownModel(id: string, name: string, caps: { efforts: Effort[]; adaptive: boolean; maxTokens: number }): ModelInfo {
  return {
    id,
    provider: "anthropic",
    label: name,
    short: name.replace(/^Claude /, ""),
    blurb: "New from Anthropic.",
    price: null,
    efforts: caps.efforts,
    defaultEffort: caps.efforts.includes("high") ? "high" : (caps.efforts[0] ?? null),
    adaptive: caps.adaptive,
    budget: !caps.adaptive,
    fallbacks: false,
    maxTokens: Math.min(64000, caps.maxTokens || 64000),
    search: "web_search_20260209",
    newer: true,
  };
}

export interface ChatConfig {
  provider: Provider;
  /** the key for that provider, in this browser */
  key: string | null;
  /** whether the other provider has a key saved too */
  otherKey: boolean;
  model: string;
  /** the effort the user picked; null keeps each model's own default */
  effort: Effort | null;
  /** Haiku's thinking, which is switched on or off rather than set by effort */
  think: boolean;
}

const KEY = "cb:anthropic";
const OPENAI_KEY = "cb:openai";
const keyName = (p: Provider) => (p === "openai" ? OPENAI_KEY : KEY);
const SETTINGS = "cb:chat:settings";

// private mode: a setting lasts as long as the tab
function write(k: string, v: string | null) {
  if (v === null) removeStored(k);
  else writeStored(k, v);
}

const agentOf = (p: Provider) => (p === "openai" ? "codex" : "claude");

/** The newer models the key's own list showed in this browser, by id: real ones, though this build has no entry for them. */
const newerIds = () => newerModels().map((m) => m.id);

/** An agent's default as the board has it now: its id, whether the board knows it, and whether the user chose it. */
export function defaultModel(p: Provider): { id: string; known: boolean; chosen: boolean } {
  return defaultFor(agentOf(p), board().runner?.agentModels, newerIds());
}

/**
 * The model a chat runs on, as the board has it now: its own, picked in its menu, else its agent's default (Features 4,
 * 26 Oct). Read at each message, so a default changed on another device holds in a tab that was open, mid-reply or not.
 * On the user's computer a default the board does not know is null, and the agent runs on its own default; on an API
 * key, which has no default of its own, it is the built-in one.
 */
export function modelFor(p: Provider, own: string | null = null, onComputer = false): string | null {
  const m = runModel(agentOf(p), board().runner?.agentModels, own, newerIds());
  return m ?? (onComputer ? null : DEFAULT_MODEL[p]);
}

/** Chooses the model an agent uses by default, on the board for every device: at once here, then on the board. null goes back to the built-in one. */
export async function chooseModel(p: Provider, model: string | null): Promise<void> {
  const agent = agentOf(p);
  const runner = board().runner;
  const before = runner?.agentModels;
  // a newer model the key's own list showed is a real one: the board keeps that it was listed, for every device
  const listed = !!model && newerIds().includes(model);
  if (runner) {
    const { [agent]: was, listed: seen = [], ...rest } = before ?? {};
    const keep = [...seen.filter((x) => x !== was || x === model), ...(listed ? [model!] : [])];
    setBoard({ runner: { ...runner, agentModels: { ...rest, ...(model ? { [agent]: model } : {}), ...(keep.length ? { listed: [...new Set(keep)] } : {}) } } });
  }
  try {
    await api("/api/chat/agent-model", { method: "PUT", body: { agent, model, ...(listed ? { listed: true } : {}) } });
  } catch (e) {
    // not saved: the choice shown goes back to what the board has
    const now = board().runner;
    if (now) setBoard({ runner: { ...now, agentModels: before } });
    throw e;
  }
}

export function loadConfig(): ChatConfig {
  let effort: Effort | null = null;
  let think = false;
  let provider: Provider = "anthropic";
  try {
    const s = JSON.parse(readStored(SETTINGS) ?? "{}") as Partial<ChatConfig> & { custom?: boolean };
    // the user's pick holds with or without a key: their own computer can run either one
    if (s.provider === "openai") provider = "openai";
    // effort saved before the levels were Claude's own (quick, balanced, thorough) meant low, medium and high
    if (s.effort && EFFORTS.some((e) => e.id === s.effort) && s.custom) effort = s.effort;
    if (typeof s.think === "boolean") think = s.think;
  } catch {
    /* defaults */
  }
  // the agent's default, chosen on the board (Features 4: a model picked in the chat is that chat's alone, and one
  // picked in this browser before there was a board's choice is no longer a second default beside it)
  const model = modelFor(provider)!;
  const other: Provider = provider === "openai" ? "anthropic" : "openai";
  return { provider, key: readStored(keyName(provider)), otherKey: !!readStored(keyName(other)), model, effort, think };
}

export function saveSettings(s: Partial<Pick<ChatConfig, "effort" | "think" | "provider">>) {
  const cur = loadConfig();
  const provider = s.provider ?? cur.provider;
  // a new provider starts at its own models' defaults; a level picked for one is not carried to the other
  const effort = s.effort !== undefined ? s.effort : provider !== cur.provider ? null : cur.effort;
  // the model is the board's (each agent's default) or the chat's own, never this browser's
  write(SETTINGS, JSON.stringify({ provider, effort, custom: effort !== null, think: s.think ?? cur.think }));
}

/** The model's settings: this build's entry, or a newer one the key's model list described. */
export function modelInfo(id: string): ModelInfo {
  return knownModel(id) ?? MODEL["claude-opus-5"];
}

/** The effort a request goes out with: the user's pick when the model takes it, else the model's own default. */
export function effortFor(m: ModelInfo, picked: Effort | null): Effort | null {
  if (!m.efforts.length) return null;
  return picked && m.efforts.includes(picked) ? picked : m.defaultEffort;
}

const NEWER = "cb:chat:newer";

// read once per saved value, so a component that asks on every render gets the same objects back
let newerCache: { raw: string | null; list: ModelInfo[] } = { raw: null, list: [] };

/** Models Anthropic lists for this key that came out after this build, remembered between visits. */
/**
 * Which of our models an id names, Claude's or Codex's: exactly, as a dated
 * release (claude-opus-5-20261001), or a newer one the key's own list showed.
 * Never a near match: claude-opus-5-5 is not claude-opus-5.
 */
export function knownModel(id: string): ModelInfo | null {
  if (Object.hasOwn(MODEL, id)) return MODEL[id];
  const dated = /^(.+)-\d{8}$/.exec(id);
  if (dated && Object.hasOwn(MODEL, dated[1])) return MODEL[dated[1]];
  return newerModels().find((m) => m.id === id) ?? null;
}

export function newerModels(): ModelInfo[] {
  const raw = readStored(NEWER);
  if (raw === newerCache.raw) return newerCache.list;
  let list: ModelInfo[] = [];
  try {
    const parsed = JSON.parse(raw ?? "[]") as ModelInfo[];
    if (Array.isArray(parsed)) list = parsed.filter((m) => m && typeof m.id === "string" && !MODEL[m.id]);
  } catch {
    /* none */
  }
  newerCache = { raw, list };
  return list;
}

export function saveNewerModels(list: ModelInfo[]) {
  write(NEWER, JSON.stringify(list.slice(0, 6)));
}

// What a key's organization turned away, remembered so the chat goes on without it: web search, the
// thinking-binding control and fallbacks on an Anthropic key, reasoning summaries on an OpenAI key
export const NO_SEARCH = "cb:chat:nosearch";
export const NO_BINDING = "cb:chat:nobinding";
export const NO_FALLBACKS = "cb:chat:nofallback";
export const NO_SUMMARY = "cb:chat:nosummary";
// NO_IMAGES: an OpenAI organization not verified for OpenAI's image models, or a model without the image tool;
// then Codex goes on without it, and draws the theme's picture or finds one instead
export const NO_IMAGES = "cb:chat:noimages";
const FLAGS: Record<Provider, string[]> = { anthropic: [NO_SEARCH, NO_BINDING, NO_FALLBACKS], openai: [NO_SUMMARY, NO_IMAGES] };

export function saveKey(key: string | null, provider: Provider = loadConfig().provider) {
  write(keyName(provider), key);
  // what one key's organization turned away says nothing about the next key's, and nothing about the
  // other provider's key, whose own stay as they were
  if (key) for (const f of FLAGS[provider]) write(f, null);
}

/** Anthropic keys start sk-ant-, OpenAI's sk- (sk-proj-, sk-svcacct-). Anything else is a paste of the wrong thing. */
export function looksLikeKey(k: string, provider: Provider = "anthropic"): boolean {
  const t = k.trim();
  return provider === "openai" ? /^sk-(?!ant-)[A-Za-z0-9_-]{20,}$/.test(t) : /^sk-ant-[A-Za-z0-9_-]{20,}$/.test(t);
}

/** Tests point the chat at a stand-in for OpenAI. Only a dev build listens. */
export function devOpenAIBase(): string | null {
  if (!import.meta.env.DEV) return null;
  return readStored("cb:openai-base");
}

/** Tests point the panel at a stand-in for Anthropic. Only a dev build listens. */
export function devBaseUrl(): string | null {
  if (!import.meta.env.DEV) return null;
  return readStored("cb:anthropic-base");
}

export interface Usage {
  input: number; // uncached input tokens
  cacheWrite: number;
  cacheRead: number;
  output: number;
  cost: number; // estimated, US dollars
  searches: number; // web searches, $10 per 1,000
  unpriced: boolean; // some of it ran on a model this build has no price for
}

export const NO_USAGE: Usage = { input: 0, cacheWrite: 0, cacheRead: 0, output: 0, cost: 0, searches: 0, unpriced: false };

/** An estimate from list prices: cache writes at 1.25x input, cache reads at 0.1x. */
export function addUsage(
  u: Usage,
  model: string,
  add: { input_tokens?: number | null; output_tokens?: number | null; cache_creation_input_tokens?: number | null; cache_read_input_tokens?: number | null; server_tool_use?: { web_search_requests?: number | null } | null },
): Usage {
  const m = knownModel(model);
  const input = add.input_tokens ?? 0;
  const cacheWrite = add.cache_creation_input_tokens ?? 0;
  const cacheRead = add.cache_read_input_tokens ?? 0;
  const output = add.output_tokens ?? 0;
  const searches = add.server_tool_use?.web_search_requests ?? 0;
  // a model this build has no price for adds tokens, and leaves the estimate where it was
  const p = m?.price;
  const cost = p ? (input * p.input + cacheWrite * p.input * 1.25 + cacheRead * p.cacheRead + output * p.output) / 1_000_000 + searches * 0.01 : 0;
  return { input: u.input + input, cacheWrite: u.cacheWrite + cacheWrite, cacheRead: u.cacheRead + cacheRead, output: u.output + output, cost: u.cost + cost, searches: u.searches + searches, unpriced: u.unpriced || !p };
}

export function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 100_000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return Math.round(n / 1000) + "k";
}

export function formatCost(usd: number): string {
  if (usd < 0.01) return "under $0.01";
  return "$" + usd.toFixed(2);
}
